import { store } from 'redux/store';
import { useDispatch } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { Share } from 'types';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateShare } from 'modules/partners/redux/sharesActions';

import { EditShareModalContextType } from '../types/EditShareModalContextType';
import { editShareModalInitValue } from '../constants/editShareModalInitValue';

const EditShareModalContext = createContext<EditShareModalContextType>(
  editShareModalInitValue
);

export const EditShareModalProvider: FC<{
  children: ReactNode;
  share?: Share;
  onSave?: () => void;
}> = ({ children, share, onSave }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const [tag, setTag] = useState<string>(share?.tag || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeTag = (newTag: string) => {
    setTag(newTag);
  };

  const handleSave = async () => {
    try {
      if (!share?._id) return;

      setIsLoading(true);

      await store.dispatch(
        updateShare({
          shareId: share._id,
          updateShareData: { tag },
        })
      );

      onSave?.();

      successSwal(t('ShareUpdatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorUpdatingShare'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  return (
    <EditShareModalContext.Provider
      value={{
        data: {
          tag,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeTag,
          handleSave,
        },
      }}
    >
      {children}
    </EditShareModalContext.Provider>
  );
};

export const useEditShareModal = () => {
  const context = useContext(EditShareModalContext);
  if (!context) {
    throw new Error(
      'useEditShareModal must be used within a EditShareModalProvider'
    );
  }
  return context;
};
